import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IDropdown, ColourPaletteType, SizeType } from '@dxp/core/models';

import {
    NgbDropdown,
    NgbDropdownModule,
    Placement,
} from '@ng-bootstrap/ng-bootstrap';
import { filter, fromEvent } from 'rxjs';

const flattenDropdownMenu = <T>(
    items: IDropdown<T>[],
): { label: string; value: T }[] => {
    const result: { label: string; value: T }[] = [];

    for (const item of items) {
        result.push({ label: item.label, value: item.value });

        if (item.subMenu?.menu?.length) {
            result.push(...flattenDropdownMenu(item.subMenu.menu));
        }
    }

    return result;
};

@Component({
    selector: 'telxl-dropdown-base',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, NgbDropdownModule],
    templateUrl: './dropdown.component.html',
    styleUrls: [
        './dropdown.component.scss',
        './../button/button.component.primary.scss',
        './../button/button.component.secondary.scss',
        './../button/button.component.success.scss',
        './../button/button.component.warning.scss',
        './../button/button.component.info.scss',
        './../button/button.component.light.scss',
        './../button/button.component.danger.scss',
        './../button/button.component.dark.scss',
        './../button/button.component.dropdown.scss',
    ],
    host: {
        '[class]': 'class || ""',
    },
})
export class TelXLDropdownBaseComponent<T> implements OnInit, OnChanges {
    @ViewChild('dropdown', { static: true }) dropdownRef!: NgbDropdown;

    readonly #destroyRef = inject(DestroyRef);
    #selectedValues: Set<T> = new Set<T>();

    @Input({ required: true }) menuItems: IDropdown<T>[] = [];
    @Input({ required: true }) colour!: ColourPaletteType;
    @Input() selected!: T | T[];
    @Input() size: SizeType | null = null;
    @Input() position: Placement = 'bottom-start';
    @Input() outline = false;
    @Input() disabled = false;
    @Input() class = '';
    @Input() text = '';
    @Input() placeholder = '';
    @Input() maxHeight = '100vh';
    @Input() multiSelect = false;

    @Output() selectedChange = new EventEmitter<T | T[]>();

    ngOnInit() {
        fromEvent<MouseEvent>(document, 'click', { capture: true })
            .pipe(
                filter(() => !!this.dropdownRef?.isOpen()),
                filter(event => {
                    const dropdownElements: HTMLElement[] = [];

                    const mainToggleEl = (this.dropdownRef as any)?._toggleEl
                        ?.nativeElement;
                    const mainMenuEl = (this.dropdownRef as any)?._menu
                        ?.nativeElement;

                    if (mainToggleEl) dropdownElements.push(mainToggleEl);
                    if (mainMenuEl) dropdownElements.push(mainMenuEl);

                    const subDropdownEls = Array.from(
                        document.querySelectorAll('.dropdown-item'),
                    ) as HTMLElement[];

                    dropdownElements.push(...subDropdownEls);

                    const clickedInside = dropdownElements.some(el =>
                        el.contains(event.target as Node),
                    );

                    return !clickedInside;
                }),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe(event => {
                event.preventDefault();
                event.stopPropagation();
                this.dropdownRef.close();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['selected'] || changes['multiSelect']) {
            this.#normalizeSelected();
        }
    }

    toggleSelection(event: Event, selected: T) {
        event.preventDefault();
        event.stopPropagation();

        if (this.multiSelect) {
            if (this.#selectedValues.has(selected)) {
                this.#selectedValues.delete(selected);
            } else {
                this.#selectedValues.add(selected);
            }
            this.#emitSelectionChange();
        } else {
            this.#selectedValues = new Set([selected]);
            this.#emitSelectionChange();
        }
    }

    handleKeyPress(event: KeyboardEvent, selected: T) {
        if (event.key === 'Enter' || event.key === ' ') {
            this.toggleSelection(event, selected);
        }
    }

    get label(): string {
        if (this.text.length > 0) return this.text;

        if (this.multiSelect) {
            const count = Array.from(this.#selectedValues).length;

            if (count === 0 && this.placeholder.length > 0) {
                return this.placeholder;
            }

            return this.placeholder.length
                ? this.placeholder + ' (' + count + ')'
                : '';
        } else {
            return (
                flattenDropdownMenu(this.menuItems).find(i =>
                    this.#isSelected(i.value),
                )?.label ?? this.placeholder
            );
        }
    }

    get dropdownClass(): string {
        let dropdownClass = 'text-start w-100 h-100 btn btn-';

        if (this.outline) dropdownClass += 'outline-';
        dropdownClass += this.colour;

        if (this.size) {
            dropdownClass += ` btn-${this.size}`;
        }

        return dropdownClass;
    }

    dropdownItemClass(item: IDropdown<T>): string {
        let dropdownClass = 'btn';

        if (this.size) {
            dropdownClass += ` btn-${this.size}`;
        }

        if (this.#isSelected(item.value)) {
            dropdownClass += ` btn-${this.outline ? 'outline-' : ''}${this.colour}`;
        }

        return dropdownClass;
    }

    #isSelected(selected: T) {
        return this.#selectedValues.has(selected);
    }

    #normalizeSelected() {
        if (this.multiSelect) {
            if (Array.isArray(this.selected)) {
                this.#selectedValues = new Set(this.selected);
            } else if (this.selected !== undefined && this.selected !== null) {
                this.#selectedValues = new Set([this.selected]);
            } else {
                this.#selectedValues = new Set();
            }
        } else {
            if (Array.isArray(this.selected)) {
                this.#selectedValues = new Set(
                    this.selected.length > 0 ? [this.selected[0]] : [],
                );
            } else if (this.selected !== undefined && this.selected !== null) {
                this.#selectedValues = new Set([this.selected]);
            } else {
                this.#selectedValues = new Set();
            }
        }
    }

    #emitSelectionChange() {
        if (this.multiSelect) {
            this.selectedChange.emit(Array.from(this.#selectedValues));
        } else {
            this.selectedChange.emit(Array.from(this.#selectedValues)[0]);
        }
    }
}
